import { isNotEmpty, type PermissionEntityId, type PermissionEntityScope } from '@terros/common'
import { type PermGraph, type PermGraphEdge, type PermGraphNode } from './types'

export type PermGraphWinningPath = Set<string>

export const toPermGraphEdgeKey = (edge: PermGraphEdge): string => `${edge.source}|${edge.target}|${edge.relation}`

export function getPermGraphWinningPath(graph: PermGraph): PermGraphWinningPath {
  const origin = graph.nodes.find((node) => node.isAppliedSource)
  if (!origin) return new Set()

  return new Set(
    [...toOriginEdges(graph, origin.entityId), ...getPermGraphCarryingEdges(graph)].map(toPermGraphEdgeKey)
  )
}

export function getPermGraphCarryingEdges(graph: PermGraph): PermGraphEdge[] {
  const origin = graph.nodes.find((node) => node.isAppliedSource)
  const target = graph.nodes.find((node) => node.isTarget)
  if (!origin || !target) return []
  return toCarryingEdges({ graph, origin, target })
}

export function isPermGraphWinningEdge(edge: PermGraphEdge, winningPath: PermGraphWinningPath): boolean {
  return winningPath.has(toPermGraphEdgeKey(edge))
}

function toOriginEdges(graph: PermGraph, originId: PermissionEntityId): PermGraphEdge[] {
  const edges: PermGraphEdge[] = []
  const visited = new Set<PermissionEntityId>([originId])
  const pending = [originId]

  while (isNotEmpty(pending)) {
    const entityId = pending.shift()
    if (!entityId) continue
    graph.edges
      .filter((edge) => edge.target === entityId && edge.relation === 'inherits')
      .forEach((edge) => {
        edges.push(edge)
        if (visited.has(edge.source)) return
        visited.add(edge.source)
        pending.push(edge.source)
      })
  }

  return edges
}

function toCarryingEdges({
  graph,
  origin,
  target,
}: {
  graph: PermGraph
  origin: PermGraphNode
  target: PermGraphNode
}): PermGraphEdge[] {
  if (origin.entityId === target.entityId) return []

  const nodes = new Map(graph.nodes.map((node) => [node.entityId, node]))
  const arrivedBy = new Map<PermissionEntityId, PermGraphEdge>()
  const visited = new Set<PermissionEntityId>([origin.entityId])
  const pending = [origin.entityId]

  while (isNotEmpty(pending)) {
    const entityId = pending.shift()
    if (!entityId) continue
    if (entityId === target.entityId) return toTrail({ arrivedBy, originId: origin.entityId, target })

    graph.edges
      .filter((edge) => edge.source === entityId)
      .filter((edge) => carriesScope({ node: nodes.get(edge.target), scope: target.flowScope, target }))
      .forEach((edge) => {
        if (visited.has(edge.target)) return
        visited.add(edge.target)
        arrivedBy.set(edge.target, edge)
        pending.push(edge.target)
      })
  }

  return []
}

function carriesScope({
  node,
  scope,
  target,
}: {
  node: PermGraphNode | undefined
  scope: PermissionEntityScope | undefined
  target: PermGraphNode
}): boolean {
  if (!node) return false
  if (node.entityId === target.entityId) return true
  return !overridesScopeAway({ node, scope })
}

function overridesScopeAway({
  node,
  scope,
}: {
  node: PermGraphNode
  scope: PermissionEntityScope | undefined
}): boolean {
  return node.configuration === 'override' && node.flowScope !== scope
}

function toTrail({
  arrivedBy,
  originId,
  target,
}: {
  arrivedBy: Map<PermissionEntityId, PermGraphEdge>
  originId: PermissionEntityId
  target: PermGraphNode
}): PermGraphEdge[] {
  const trail: PermGraphEdge[] = []
  const walk = (entityId: PermissionEntityId): void => {
    const edge = arrivedBy.get(entityId)
    if (!edge || edge.source === entityId) return
    trail.unshift(edge)
    if (edge.source === originId) return
    walk(edge.source)
  }

  walk(target.entityId)
  return trail
}
